import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Banana } from 'lucide-react';
import { X, Lightning, Sparkle, ArrowUp, CreditCard } from '@phosphor-icons/react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import billingService from '../../services/billing';
import { useNavigate } from 'react-router-dom';

// Out-of-credits modal that appears when a generation fails with a 402.
// Pulls live credit-pack listings from /billing/credit-packs and lets the
// user buy a pack in one click — Stripe Checkout handles their saved
// card automatically if they have one. Also offers a route to the
// subscription upgrade flow for users who'd rather move up a plan than
// buy a pack.
const CreditAlertModal = () => {
    const { creditAlert, closeCreditAlert } = useApp();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [packs, setPacks] = useState([]);
    const [loadingPacks, setLoadingPacks] = useState(false);
    const [purchasing, setPurchasing] = useState(null); // pack slug being purchased
    const [error, setError] = useState(null);

    const hasCardOnFile = !!user?.stripeCustomerId;

    // Fetch credit packs only when the modal opens, then cache.
    useEffect(() => {
        if (!creditAlert.isOpen) return;
        if (packs.length > 0) return;
        setLoadingPacks(true);
        setError(null);
        billingService.getCreditPacks()
            .then((res) => {
                const list = res?.data?.creditPacks || res?.data?.packs || res?.creditPacks || res?.packs || [];
                setPacks(list);
            })
            .catch((err) => {
                console.warn('[credit-alert] load packs failed', err);
                setError('Could not load credit packs. Try the billing portal directly.');
            })
            .finally(() => setLoadingPacks(false));
    }, [creditAlert.isOpen, packs.length]);

    const handleBuy = async (pack) => {
        setPurchasing(pack.slug);
        setError(null);
        try {
            const res = await billingService.checkoutCredits({ packId: pack.slug });
            const url = res?.data?.url || res?.url;
            if (url) {
                window.location.href = url;
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (err) {
            console.error('[credit-alert] checkout failed', err);
            setError(err?.response?.data?.error?.message || err?.message || 'Checkout failed — try again.');
            setPurchasing(null);
        }
    };

    const handleUpgrade = () => {
        closeCreditAlert();
        navigate('/account?tab=billing');
    };

    // Pick the pack that fills the requested credit gap most cheaply.
    // We highlight it as the recommended choice so the user can one-click
    // exactly what they need without doing pack-size math.
    const required = creditAlert.required || 0;
    const available = creditAlert.available || 0;
    const gap = Math.max(0, required - available);
    const recommendedPack = packs
        .slice()
        .sort((a, b) => (a.priceCents || 0) - (b.priceCents || 0))
        .find((p) => (p.credits + (p.bonusCredits || 0)) >= gap) || packs[packs.length - 1];

    return (
        <AnimatePresence>
            {creditAlert.isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeCreditAlert}
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 12 }}
                        className="relative w-full max-w-lg bg-[#0f172a] rounded-3xl border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.6)] overflow-hidden"
                    >
                        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#FBbf24]/15 to-transparent pointer-events-none" />
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#FBbf24]/10 rounded-full blur-3xl pointer-events-none" />

                        <button
                            onClick={(e) => { e.stopPropagation(); closeCreditAlert(); }}
                            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors p-2 z-50"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>

                        <div className="relative z-10 p-7 flex flex-col">
                            {/* Header */}
                            <div className="flex items-center gap-4 mb-5">
                                <motion.div
                                    initial={{ scale: 0, rotate: -45 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    transition={{ type: 'spring', delay: 0.05 }}
                                    className="w-14 h-14 bg-gradient-to-br from-[#FBbf24] to-[#d97706] rounded-2xl flex items-center justify-center shadow-[0_8px_24px_rgba(251,191,36,0.3)] shrink-0 rotate-3"
                                >
                                    <Banana size={28} className="text-black drop-shadow-md" />
                                </motion.div>
                                <div className="min-w-0">
                                    <h2 className="text-xl font-bold text-white">Out of credits</h2>
                                    <p className="text-xs text-white/55 mt-0.5">
                                        You need <span className="text-[#FBbf24] font-bold">{gap.toLocaleString()}</span> more
                                        {' '}credit{gap !== 1 ? 's' : ''} for this generation.
                                    </p>
                                </div>
                            </div>

                            {/* Required vs available */}
                            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-5 flex justify-between items-center text-sm">
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-white/40 uppercase tracking-widest">Required</span>
                                    <span className="font-bold text-white font-code">{required.toLocaleString()}</span>
                                </div>
                                <div className="text-white/30">→</div>
                                <div className="flex flex-col items-end">
                                    <span className="text-[10px] text-white/40 uppercase tracking-widest">You have</span>
                                    <span className="font-bold text-[#FBbf24] font-code">{available.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Card-on-file hint */}
                            {hasCardOnFile && !error && (
                                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-cyan-400/8 border border-cyan-400/20 text-[11px] text-cyan-100">
                                    <CreditCard size={14} weight="fill" className="text-cyan-300 shrink-0" />
                                    <span>One click charges your card on file.</span>
                                </div>
                            )}

                            {/* Error */}
                            {error && (
                                <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-200">
                                    {error}
                                </div>
                            )}

                            {/* Credit packs */}
                            <div className="mb-5">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Top up</span>
                                    {recommendedPack && (
                                        <span className="text-[10px] text-[#FBbf24]/80 uppercase tracking-widest font-bold flex items-center gap-1">
                                            <Sparkle size={10} weight="fill" /> {recommendedPack.name} covers it
                                        </span>
                                    )}
                                </div>

                                {loadingPacks && (
                                    <div className="flex items-center justify-center py-8 text-white/30 text-xs">
                                        Loading packs…
                                    </div>
                                )}

                                {!loadingPacks && packs.length === 0 && !error && (
                                    <div className="flex items-center justify-center py-6 text-white/40 text-xs">
                                        No credit packs available.
                                    </div>
                                )}

                                {!loadingPacks && packs.length > 0 && (
                                    <div className="grid grid-cols-1 gap-2">
                                        {packs.map((pack) => {
                                            const isRecommended = recommendedPack?.slug === pack.slug;
                                            const isBuying = purchasing === pack.slug;
                                            const totalCredits = pack.credits + (pack.bonusCredits || 0);
                                            return (
                                                <button
                                                    key={pack.slug}
                                                    onClick={() => handleBuy(pack)}
                                                    disabled={!!purchasing}
                                                    className={`group relative w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed
                                                        ${isRecommended
                                                            ? 'border-[#FBbf24]/40 bg-[#FBbf24]/8 hover:bg-[#FBbf24]/14 shadow-[0_0_20px_rgba(251,191,36,0.08)]'
                                                            : 'border-white/10 bg-white/3 hover:bg-white/6 hover:border-white/20'}`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                                            ${isRecommended ? 'bg-[#FBbf24]/15 text-[#FBbf24]' : 'bg-white/8 text-white/50'}`}>
                                                            <Banana size={18} fill={isRecommended ? '#FBbf24' : 'currentColor'} />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-bold text-white">{pack.name}</div>
                                                            <div className="text-[11px] text-white/50">
                                                                {totalCredits.toLocaleString()} credits
                                                                {pack.bonusCredits > 0 && (
                                                                    <span className="text-emerald-300/80"> · +{pack.bonusCredits.toLocaleString()} bonus</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="text-base font-bold text-white font-code">
                                                            ${((pack.priceCents || 0) / 100).toFixed(2)}
                                                        </span>
                                                        {isBuying ? (
                                                            <Lightning size={16} weight="fill" className="text-[#FBbf24] animate-pulse" />
                                                        ) : (
                                                            <ArrowUp size={14} weight="bold" className="text-white/30 group-hover:text-white rotate-45 transition-transform" />
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Upgrade plan */}
                            <button
                                onClick={handleUpgrade}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500/15 to-blue-500/15 hover:from-cyan-500/25 hover:to-blue-500/25 border border-cyan-400/20 hover:border-cyan-400/40 text-sm text-cyan-100 font-bold flex items-center justify-center gap-2 transition-all"
                            >
                                <ArrowUp size={14} weight="bold" />
                                Or upgrade your plan for monthly credits
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreditAlertModal;
